import React, { useEffect } from 'react';
import { UsersIcon, ClockIcon, AlertCircleIcon, TruckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { isStringKey } from '../../data/i18n';
import { isWeekend, offerMayApply, weekdayOffer } from '../../data/offers';
import { hrs, usd } from '../../utils/format';
import { HelpAnswers } from './HelpAnswers';
const estCopy = {
  en: {
    noHidden: 'No hidden fees. Every charge is listed before you book.',
    mode: 'Pricing: hourly estimate',
    modeNote: 'Final cost follows actual hours at the accepted rate. A maximum price for your declared items, access and services becomes available once our quote system issues it — it is not offered yet.',
    truck: 'Truck you requested',
    truckHelp: 'Help me choose',
    truckNone: 'No preference',
    truckSub: 'Your preference — dispatch confirms the assigned truck.',
    services: 'Extra services',
    servicesValue: 'Time and any charge confirmed by our team before you book',
    stops: 'Extra stops',
    svc: {
      disassembly: 'Disassembly',
      reassembly: 'Reassembly',
      placement: 'Room placement',
      garageOnly: 'Garage-only delivery',
      unpacking: 'Unpacking'
    },
    offer: `You may qualify for our weekday rate from $${weekdayOffer.from}/h for 2 movers. Our team confirms eligibility before you book; the estimate below uses the standard rate.`,
    taxes: 'Taxes and payment-method charges',
    taxesValue: 'None added — any applicable charge is shown before payment'
  },
  es: {
    noHidden: 'Sin cargos ocultos. Cada cargo aparece antes de reservar.',
    mode: 'Precio: estimado por hora',
    modeNote: 'El costo final sigue las horas reales a la tarifa aceptada. Un precio máximo para sus artículos, acceso y servicios declarados estará disponible cuando nuestro sistema de cotización lo emita; todavía no se ofrece.',
    truck: 'Camión que solicitó',
    truckHelp: 'Ayúdenme a elegir',
    truckNone: 'Sin preferencia',
    truckSub: 'Es su preferencia; despacho confirma el camión asignado.',
    services: 'Servicios adicionales',
    servicesValue: 'Tiempo y cualquier cargo confirmados por nuestro equipo antes de reservar',
    stops: 'Paradas adicionales',
    svc: {
      disassembly: 'Desarme',
      reassembly: 'Armado',
      placement: 'Colocación por habitación',
      garageOnly: 'Entrega en garaje',
      unpacking: 'Desempaque'
    },
    offer: `Podría calificar para nuestra tarifa entre semana desde $${weekdayOffer.from}/h con 2 trabajadores. Nuestro equipo confirma si aplica antes de reservar; el estimado usa la tarifa estándar.`,
    taxes: 'Impuestos y cargos por método de pago',
    taxesValue: 'No se agregan; cualquier cargo aplicable se muestra antes de pagar'
  }
};
export function StepEstimate() {
  const {
    t,
    lang,
    quote,
    data,
    track
  } = useFunnel();
  const crew = quote.crew;
  const x = estCopy[lang];
  useEffect(() => {
    track('EstimatePresented', {
      quote: quote.hash
    });
  }, [track, quote.hash]);
  const truck = data.truckPreference ? data.truckPreference === 'help' ? x.truckHelp : `${data.truckPreference} ft` : x.truckNone;
  const svc = [data.needsDisassembly && x.svc.disassembly, data.services.reassembly && x.svc.reassembly, data.services.placement && x.svc.placement, data.services.garageOnly && x.svc.garageOnly, data.services.unpacking && x.svc.unpacking].filter(Boolean) as string[];
  const offer = offerMayApply(data, crew.used) && !isWeekend(data.moveDate);
  return <div data-id="element-715" data-mp="744o8m1gtgv3">
      <div className="mb-7" data-id="element-716" data-mp="27ebo8ovd3ef">
        <h1 className="font-display text-3xl font-black leading-tight text-magenta sm:text-4xl" data-id="element-717" data-mp="285wcogxhgpx">{t('estTitle')}</h1>
        <p className="mt-2 max-w-2xl text-base font-semibold leading-relaxed text-ink" data-id="element-718" data-mp="gx2cz9x1w225">{x.noHidden}</p>
        <p className="mt-1 max-w-2xl text-base leading-relaxed text-ink-600" data-id="element-719" data-mp="c4sfn8tlvzpu">{t('estSub')}</p>
      </div>

      <section className="rounded-2xl bg-ink text-white" data-id="element-720" data-mp="3kjsxzb31nu0d">
        <div className="grid gap-6 p-6 sm:grid-cols-2" data-id="element-721" data-mp="3fmsriz2a8p8q">
          <div data-id="element-722" data-mp="3fk6jyzfe69qi">
            <p className="flex items-center gap-2 text-sm font-medium text-ink-300" data-id="element-723" data-mp="2iuvzk2o9ia6w">
              <UsersIcon className="h-4 w-4" aria-hidden="true" data-id="element-724" data-mp="2ist24xks5es5" />
              {t('crewLabel')} · {crew.used === crew.recommended ? t('recommended') : t('yourChoice')}
            </p>
            <p className="mt-2 font-display text-4xl font-black" data-id="element-725" data-mp="2ruao2wkvcaor">{t('moversN', {
              n: crew.used
            })}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-200" data-id="element-726" data-mp="2wnlnwdr9jr76">{isStringKey(crew.reasonKey) ? t(crew.reasonKey) : ''}</p>
          </div>
          <div data-id="element-727" data-mp="36jyqk14sdfe2">
            <p className="flex items-center gap-2 text-sm font-medium text-ink-300" data-id="element-728" data-mp="36jowo2gmeo33">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-729" data-mp="3bj1neyam4fay" />
              {t('serviceLabel')}
            </p>
            <p className="mt-2 font-display text-4xl font-black text-lime" data-id="element-730" data-mp="3fk4zratgbt7f">{t('hoursRange', {
              a: hrs(quote.serviceLow),
              b: hrs(quote.serviceHigh)
            })}</p>
            <p className="mt-3 text-sm text-ink-300" data-id="element-731" data-mp="1h7wmi1qwugzb">{t('minimumNote')}</p>
            <p className="mt-4 flex items-center gap-2 text-sm text-ink-200" data-id="element-732" data-mp="1l7f70wetkq1m">
              <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-733" data-mp="1doeaww6clyoa" />
              {x.truck}: <span className="font-semibold text-white" data-id="element-734" data-mp="18wnv4aon6iph">{truck}</span>
            </p>
          </div>
        </div>
      </section>

      {offer && <p className="mt-4 rounded-xl bg-lime-soft p-4 text-sm font-medium text-ink" data-id="element-735" data-mp="13y2u491kfuzz">{x.offer}</p>}

      {quote.reviewReasons.length > 0 && <section className="mt-6 rounded-xl border border-ink-300 bg-white p-5" data-id="element-736" data-mp="1wm8e2tkspfyr">
          <h2 className="flex items-center gap-2 font-semibold text-ink" data-id="element-737" data-mp="1wk4akcqt6iz0">
            <AlertCircleIcon className="h-5 w-5 text-magenta" aria-hidden="true" data-id="element-738" data-mp="1ro6ve9ra7int" />
            {t('reviewTitle')}
          </h2>
          <ul className="mt-3 space-y-1.5 pl-7 text-[15px] text-ink-700" data-id="element-739" data-mp="1impl580k2b71">
            {quote.reviewReasons.map(r => <li key={r} className="list-disc" data-id="element-740" data-mp="c76ifbr8rwea">
                {isStringKey(r) ? t(r) : r}
              </li>)}
          </ul>
        </section>}

      <section className="mt-8" data-id="element-741" data-mp="79wvc2rbrwtm">
        <p className="mb-2 text-sm font-semibold text-ink" data-id="element-742" data-mp="8o7kvbt7lic0">{x.mode}</p>
        <dl className="divide-y divide-ink-200 rounded-2xl bg-white px-5 text-[15px] ring-1 ring-ink-200" data-id="element-743" data-mp="dnsnbw7l88th">
          <Row label={t('lineRate')} sub={t('rateLabel', {
          n: crew.used
        })} value={quote.hourlyRate !== null ? `${usd(quote.hourlyRate, lang)}/h` : t('rateByTeam')} data-id="element-744" data-mp="3vtfcp4aoflb3" />
          <Row label={t('lineService')} value={t('hoursRange', {
          a: hrs(quote.serviceLow),
          b: hrs(quote.serviceHigh)
        })} data-id="element-745" data-mp="3ntgq4uqasj9" />
          <Row label={t('lineTravel')} value={t('hoursN', {
          n: quote.travelHours
        })} data-id="element-746" data-mp="3quc8fm2itj46" />
          <Row label={t('lineLabor')} value={quote.laborLow !== null && quote.laborHigh !== null ? `${usd(quote.laborLow, lang)} – ${usd(quote.laborHigh, lang)}` : '—'} strong data-id="element-747" data-mp="3qwf5gq23ccie" />
          <Row label={t('lineSurcharge')} value={quote.surchargeBaseApproved ? '—' : t('surchargePending')} muted data-id="element-748" data-mp="3h52jug6vyikt" />
          {quote.packingSelected && <Row label={t('linePacking')} value={t('pricedByTeam')} muted data-id="element-749" data-mp="383l44yxe4iwn" />}
          {svc.length > 0 && <Row label={x.services} sub={svc.join(' · ')} value={x.servicesValue} muted data-id="element-750" data-mp="2oi1fldekvkxp" />}
          {data.stops.length > 0 && <Row label={x.stops} value={String(data.stops.length)} sub={t('rr_stops')} data-id="element-751" data-mp="2jjhoyzp1hkz0" />}
          <Row label={x.taxes} value={x.taxesValue} muted data-id="element-752" data-mp="2xihesaoag5wr" />
          <Row label={t('lineDeposit')} value={usd(quote.deposit, lang)} sub={t('depositCredit')} strong data-id="element-753" data-mp="2xk28jy2s3ktk" />
          <Row label={t('lineRemaining')} value={quote.remainingLow !== null && quote.remainingHigh !== null ? `${usd(quote.remainingLow, lang)} – ${usd(quote.remainingHigh, lang)}` : '—'} sub={t('remainingNote')} data-id="element-754" data-mp="2tj8lf6x5ua1q" />
        </dl>
        <p className="mt-3 text-sm text-ink-600" data-id="element-755" data-mp="3h5f3yn47n867">{x.modeNote}</p>
        <p className="mt-3 font-mono text-xs text-ink-400" data-id="element-756" data-mp="3c73sk7y9oplv">
          {quote.configVersion} · #{quote.hash}
        </p>
        <p className="mt-4 text-sm text-ink-600" data-id="element-757" data-mp="3q8g9all94v8t">{t('acceptNote')}</p>
      </section>

      <HelpAnswers screen="stepEstimate" className="mt-10" data-id="element-758" data-mp="3l8tsk267swv9" />
    </div>;
}
function Row({
  label,
  value,
  sub,
  strong,
  muted
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 py-3.5" data-id="element-759" data-mp="11d52akmb8ra1">
      <dt className={strong ? 'font-semibold text-ink' : 'text-ink-700'} data-id="element-760" data-mp="15fjj6z6f5gyo">{label}</dt>
      <dd className={`text-right ${strong ? 'font-bold text-ink' : muted ? 'max-w-[240px] text-sm text-ink-500' : 'font-semibold text-ink'}`} data-id="element-761" data-mp="1aew6fl17cq1s">{value}</dd>
      {sub && <p className="col-span-2 text-sm text-ink-500" data-id="element-762" data-mp="dqq81fuvitvy">{sub}</p>}
    </div>;
}